import { Typography, Stack, Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useNotificationHandling } from "../utils/notificationHandle";
import { postErrorData } from "../utils/apis";

const isChunkLoadError = (error) => {
  const msg = error?.message || "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("is not a valid JavaScript MIME type")
  );
};

export default function ErrorFallback({ error, _resetErrorBoundary }) {
  const { handleErrorNotification } = useNotificationHandling();
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    console.error("Error object ErrorFallback:", error);
    if (!error) {
      console.error("Error is undefined or null");
      return;
    }

    // Auto-reload on stale chunk errors with cache-busting
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem("chunk_reload_ts");
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 10000) {
        setShowSpinner(true);
        sessionStorage.setItem("chunk_reload_ts", String(now));
        const url = new URL(window.location.href);
        url.searchParams.set("_cb", String(now));
        window.location.replace(url.toString());
        return;
      }
    }

    handleOnError(error, handleErrorNotification);
  }, [error, handleErrorNotification]);

  // Show a loading spinner while auto-reloading for chunk errors
  if (showSpinner) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack
      sx={{
        width: "100vw",
        height: "100vh",
        background: "#020817",
        color: "white",
      }}
      alignItems="center"
      justifyContent="center"
    >
      <Typography
        variant="h2"
        fontWeight={500}
        sx={{ width: "100%", maxWidth: "600px", textAlign: "center" }}
      >
        Something went wrong.
      </Typography>
      <Typography
        variant="h3"
        fontWeight={500}
        sx={{
          width: "100%",
          maxWidth: "600px",
          textAlign: "center",
          lineHeight: 1.2,
          mt: 1,
        }}
      >
        Please refresh the page and try again. If the issue persists, contact
        support for assistance.
      </Typography>
    </Stack>
  );
}

const handleOnError = async (error, _handleErrorNotification) => {
  if (!error) {
    console.error("Error is undefined or null");
    return;
  }

  console.error("An error occurred:", error);

  const name = error?.name || "";
  const message = error?.message || "";
  const code = error?.code || "";
  const stack = error?.stack || "";

  try {
    await postErrorData({
      name: name,
      message: message,
      code: code,
      description: stack,
      source: "React",
    });
  } catch (error) {
    console.error(error);
  }
};
